/**
 * useEntitlement — the inline client-side gating primitive.
 *
 * Given a permission code (e.g. `document:generate`), returns whether the
 * current user holds it, which plan grants it, and where to send them to
 * upgrade. This is the cheap, client-side first gate for gated actions; the
 * backend remains the source of truth (a stale-cache miss is caught by the
 * global 403 interceptor in `APIAgent`, which surfaces the same upgrade modal).
 *
 * Reads `permissions` from the authenticated user object. The user comes from
 * `useAuth()` (authAgent) and mirrors `GET /api/auth/me`, whose `PersonSchema`
 * response includes `permissions: string[]` and `subscription_plans: string[]`.
 *
 *   const { hasPermission, requiredPlan, upgradeUrl } = useEntitlement('document:generate');
 *
 * @param {string} permission - Permission code to check (e.g. 'document:generate').
 * @param {object} [user] - Optional explicit user object. When omitted, the hook
 *   reads from the auth context via {@link useAuth}. Passing it directly lets a
 *   consumer that already holds `user` avoid an extra `/me` validation pass and
 *   keeps the hook trivially testable.
 * @returns {{ hasPermission: boolean, requiredPlan: ('buyer'|'fullservice'|null), upgradeUrl: string }}
 */

import { useMemo } from 'react';
import { useAuth } from '../agents/authAgent';

/** Pricing route the upgrade CTAs point at (the app has no `/billing` settings
 *  page until PR4; pricing is the canonical "compare/upgrade" surface). */
export const PRICING_URL = '/pricing';

/**
 * Best-effort static map of permission → the lowest plan that grants it.
 *
 * PR3 scope: a small documented map is sufficient. The authoritative grants
 * live server-side on each `SubscriptionPlan.permissions`; this mirror only
 * drives the upgrade-prompt copy ("…Buyer Paperwork…" vs "…Full Service…").
 * Keep it in sync with the backend plan seeds as new gated permissions land.
 *
 * Tiers:
 *   - 'buyer'       → the $499 Buyer Paperwork plan (offer paperwork, e-sign and
 *     AI suggestions for a home not listed on cutthe6.com).
 *   - 'fullservice' → the flat Full Service listing fee — $799, or $1,499 at a
 *     $1M+ list price (seller surface).
 */
export const PERMISSION_PLAN_MAP = {
  // Buyer Paperwork (buyer paperwork + AI)
  'offer:create': 'buyer',
  'document:generate': 'buyer',
  'document:esign': 'buyer',
  'ai:negotiation': 'buyer',
  // Full Service (seller surface)
  'seller:dashboard': 'fullservice',
  'ai:pricing': 'fullservice',
  'closing:coordinate': 'fullservice',
};

/** Default plan to pitch when a gated permission isn't in the static map. */
const DEFAULT_REQUIRED_PLAN = 'buyer';

/**
 * Resolve the plan that grants a permission. Exported for the 403 interceptor
 * path, which needs the same mapping without a React render.
 *
 * @param {string} permission
 * @returns {'buyer'|'fullservice'}
 */
export function planForPermission(permission) {
  return PERMISSION_PLAN_MAP[permission] || DEFAULT_REQUIRED_PLAN;
}

/**
 * Build the upgrade URL for a given required plan. Appends a `?plan=` hint so the
 * pricing surface can highlight the right card.
 *
 * @param {('buyer'|'fullservice'|null)} requiredPlan
 * @returns {string}
 */
export function upgradeUrlFor(requiredPlan) {
  return requiredPlan ? `${PRICING_URL}?plan=${requiredPlan}` : PRICING_URL;
}

export function useEntitlement(permission, user) {
  // Only call the auth context when the caller didn't hand us a user. Hooks
  // can't be conditional, so `useAuth()` always runs, but we ignore its result
  // when an explicit `user` was supplied.
  const auth = useAuth();
  const effectiveUser = user !== undefined ? user : auth.user;

  return useMemo(() => {
    const permissions = Array.isArray(effectiveUser?.permissions)
      ? effectiveUser.permissions
      : [];
    const hasPermission = permissions.includes(permission);
    // Only surface a required plan when the user actually lacks the permission.
    const requiredPlan = hasPermission ? null : planForPermission(permission);
    return {
      hasPermission,
      requiredPlan,
      upgradeUrl: upgradeUrlFor(requiredPlan),
    };
  }, [effectiveUser, permission]);
}

export default useEntitlement;
